import { gql } from '@apollo/client';

export type LoadingPolicyOption = { value: string; label: string };

export type LoadingPolicyParamSpec = {
  name: string;
  label: string;
  /** ENUM 单选 / ENUM_SET 多选 / LONG_SET 整数列表 */
  type: 'ENUM' | 'ENUM_SET' | 'LONG_SET';
  required: boolean;
  options: LoadingPolicyOption[];
  description: string | null;
  visibleWhen: { param: string; value: string } | null;
};

export type LoadingPolicyCheckSpec = {
  code: string;
  label: string;
  description: string;
  mandatory: boolean;
  repeatableBy: string | null;
  order: number;
  allowedOnFail: LoadingPolicyOption[];
  params: LoadingPolicyParamSpec[];
};

export type LoadingPolicyCatalog = {
  loadingUnits: LoadingPolicyOption[];
  destinationWaypointTypes: LoadingPolicyOption[];
  routeTypes: LoadingPolicyOption[];
  businessTypes: LoadingPolicyOption[];
  taskTypes: LoadingPolicyOption[];
  addressingModes: LoadingPolicyOption[];
  checks: LoadingPolicyCheckSpec[];
  sideEffects: LoadingPolicyOption[];
  fixedChecks: LoadingPolicyOption[];
};

export type LoadingPolicyCheckItem = { code: string; paramsJson: string | null; onFail: string | null };

export type LoadingPolicyItem = {
  id: string;
  name: string;
  loadingUnit: string;
  destinationWaypointTypes: string[];
  routeTypes: string[] | null;
  businessTypes: string[] | null;
  taskTypes: string[] | null;
  addressing: string;
  checks: LoadingPolicyCheckItem[];
  sideEffects: string[];
  checksJson: string | null;
  sideEffectsJson: string | null;
  version: number;
  isDeleted: boolean;
  valid: boolean;
  parseError: string | null;
  updatedAt: number | null;
};

export type LoadingPolicyIssue = { path: string; message: string };

export type LoadingPolicyValidation = {
  valid: boolean;
  errors: LoadingPolicyIssue[];
  warnings: LoadingPolicyIssue[];
  checksJson: string | null;
  sideEffectsJson: string | null;
};

export type LoadingPolicySaveResult = {
  policy: LoadingPolicyItem | null;
  validation: LoadingPolicyValidation;
};

export type LoadingPolicyInput = {
  name: string;
  loadingUnit: string;
  destinationWaypointTypes: string[];
  routeTypes: string[] | null;
  businessTypes: string[] | null;
  taskTypes: string[] | null;
  addressing: string;
  checks: LoadingPolicyCheckItem[];
  sideEffects: string[];
};

export type LoadingPolicyLog = {
  id: string;
  policyId: string;
  action: 'CREATE' | 'UPDATE' | 'DELETE';
  beforeJson: string | null;
  afterJson: string | null;
  operatorId: string | null;
  operatorName: string | null;
  createdAt: number | null;
};

const OPTION = 'value label';

export const GET_LOADING_POLICY_CATALOG = gql`
  query GetLoadingPolicyCatalog {
    getLoadingPolicyCatalog {
      loadingUnits { ${OPTION} }
      destinationWaypointTypes { ${OPTION} }
      routeTypes { ${OPTION} }
      businessTypes { ${OPTION} }
      taskTypes { ${OPTION} }
      addressingModes { ${OPTION} }
      sideEffects { ${OPTION} }
      fixedChecks { ${OPTION} }
      checks {
        code
        label
        description
        mandatory
        repeatableBy
        order
        allowedOnFail { ${OPTION} }
        params {
          name
          label
          type
          required
          description
          options { ${OPTION} }
          visibleWhen { param value }
        }
      }
    }
  }
`;

const POLICY_FIELDS = `
  id
  name
  loadingUnit
  destinationWaypointTypes
  routeTypes
  businessTypes
  taskTypes
  addressing
  checks { code paramsJson onFail }
  sideEffects
  checksJson
  sideEffectsJson
  version
  isDeleted
  valid
  parseError
  updatedAt
`;

const VALIDATION_FIELDS = `
  valid
  errors { path message }
  warnings { path message }
  checksJson
  sideEffectsJson
`;

export const GET_LOADING_POLICIES = gql`
  query GetLoadingPolicies($includeDeleted: Boolean) {
    getLoadingPolicies(includeDeleted: $includeDeleted) { ${POLICY_FIELDS} }
  }
`;

export const GET_LOADING_POLICY_LOGS = gql`
  query GetLoadingPolicyLogs($policyId: Long!) {
    getLoadingPolicyLogs(policyId: $policyId) {
      id
      policyId
      action
      beforeJson
      afterJson
      operatorId
      operatorName
      createdAt
    }
  }
`;

export const VALIDATE_LOADING_POLICY = gql`
  mutation ValidateLoadingPolicy($input: LoadingPolicyInput!, $id: Long) {
    validateLoadingPolicy(input: $input, id: $id) { ${VALIDATION_FIELDS} }
  }
`;

export const CREATE_LOADING_POLICY = gql`
  mutation CreateLoadingPolicy($input: LoadingPolicyInput!) {
    createLoadingPolicy(input: $input) {
      policy { ${POLICY_FIELDS} }
      validation { ${VALIDATION_FIELDS} }
    }
  }
`;

export const UPDATE_LOADING_POLICY = gql`
  mutation UpdateLoadingPolicy($id: Long!, $version: Int!, $input: LoadingPolicyInput!) {
    updateLoadingPolicy(id: $id, version: $version, input: $input) {
      policy { ${POLICY_FIELDS} }
      validation { ${VALIDATION_FIELDS} }
    }
  }
`;

export const DELETE_LOADING_POLICY = gql`
  mutation DeleteLoadingPolicy($id: Long!, $version: Int!) {
    deleteLoadingPolicy(id: $id, version: $version) {
      policy { ${POLICY_FIELDS} }
      validation { ${VALIDATION_FIELDS} }
    }
  }
`;
